import { useEffect, useRef, useState, useCallback } from 'react'
import { WS_BASE_URL } from '../config'

const usePostsWebSocket = (isAuthenticated, onNewPosts) => {
  const [wsConnected, setWsConnected] = useState(false)
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5
  const isConnectingRef = useRef(false)

  // Keep the callback ref current without it being a dep of connect().
  // This prevents connect() from being recreated on every render,
  // which was causing the stale-closure double-connection bug.
  const onNewPostsRef = useRef(onNewPosts)
  useEffect(() => { onNewPostsRef.current = onNewPosts }, [onNewPosts])

  const connect = useCallback(() => {
    if (!isAuthenticated) return
    // Guard: don't open a second connection if one is already open or opening.
    if (isConnectingRef.current) return
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    isConnectingRef.current = true

    const ws = new WebSocket(`${WS_BASE_URL}/ws/posts/`)
    wsRef.current = ws

    ws.onopen = () => {
      setWsConnected(true)
      reconnectAttempts.current = 0
      isConnectingRef.current = false
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (['posts_initial', 'more_posts', 'new_posts_batch'].includes(message.type)) {
          if (message.data) {
            // Pass the message type so the consumer can distinguish reconnect
            // initial batches from genuine load-more exhaustion.
            onNewPostsRef.current(message.data, message.type)
          }
        } else if (message.type === 'error') {
          console.error('WebSocket error from server:', message.message)
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err)
      }
    }

    ws.onerror = () => {
      setWsConnected(false)
      isConnectingRef.current = false
    }

    ws.onclose = () => {
      setWsConnected(false)
      isConnectingRef.current = false
      if (reconnectAttempts.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000)
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttempts.current += 1
          connect()
        }, delay)
      }
    }
  }, [isAuthenticated]) // onNewPosts intentionally excluded — handled via ref

  useEffect(() => {
    connect()
    return () => {
      isConnectingRef.current = false
      clearTimeout(reconnectTimeoutRef.current)
      if (wsRef.current) {
        // Null out onclose BEFORE closing so the cleanup doesn't trigger
        // the reconnect logic, which would race with the new effect call.
        wsRef.current.onclose = null
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close()
        }
      }
    }
  }, [connect])

  const requestMorePosts = useCallback((page = 1) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'request_more_posts', page }))
    }
  }, [])

  const notifyReachedEnd = useCallback((page = 1) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'reached_end', page }))
    }
  }, [])

  return { wsConnected, requestMorePosts, notifyReachedEnd, wsRef }
}

export default usePostsWebSocket
